import { useMemo, useState } from "react";
import { hotPosts, trends, type HotPost } from "@/mocks/gua";

interface FeedSectionProps {
  query: string;
}

function formatCount(n: number) {
  if (n >= 10000) return `${(n / 10000).toFixed(n >= 100000 ? 0 : 1)}万`;
  return String(n);
}

const tabs = ["全部", "影视综艺", "社会热点", "娱乐八卦", "科技互联网", "生活趣闻", "体育电竞"];

function HotPostCard({ post, rank }: { post: HotPost; rank: number }) {
  const [liked, setLiked] = useState(false);
  return (
    <article className="group rounded-2xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-background-300 md:p-5">
      <div className="flex gap-4">
        <div className="flex flex-col items-center pt-1">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-full text-lg ${
              rank === 1
                ? "bg-primary-100 text-primary-600"
                : rank === 2
                  ? "bg-accent-100 text-accent-700"
                  : "bg-secondary-100 text-secondary-800"
            }`}
            aria-hidden="true"
          >
            {post.emoji}
          </span>
          <span className="mt-2 text-xs font-semibold text-foreground-400">#{rank}</span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
              {post.category}
            </span>
            {post.pinned && (
              <span className="inline-flex items-center gap-1 rounded-md bg-accent-100 px-2 py-0.5 text-xs font-semibold text-accent-700">
                <i className="ri-pushpin-line text-xs" aria-hidden="true" />置顶
              </span>
            )}
            {post.fresh && (
              <span className="inline-flex items-center gap-1 rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-semibold text-secondary-800">
                <i className="ri-flashlight-line text-xs" aria-hidden="true" />新瓜
              </span>
            )}
          </div>

          <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
            {post.title}
          </h3>
          <p className="mt-1.5 hidden text-sm leading-relaxed text-foreground-600 sm:block">
            {post.summary}
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-secondary-200 text-[9px] font-bold text-secondary-800">
                {post.author.charAt(0)}
              </span>
              <span className="font-medium text-foreground-700">{post.author}</span>
              <span className="hidden text-foreground-400 md:inline">· {post.authorTag}</span>
            </span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <i className="ri-time-line" aria-hidden="true" />
              {post.time}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-dashed border-background-200 pt-3">
        <div className="flex flex-wrap gap-1.5">
          {post.tags.map((t) => (
            <span
              key={t}
              className="whitespace-nowrap rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-500"
            >
              {t}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <i className="ri-chat-3-line text-base text-primary-500" aria-hidden="true" />
            <b className="font-semibold text-foreground-700">{formatCount(post.discussions)}</b>讨论
          </span>
          <button
            type="button"
            onClick={() => setLiked((v) => !v)}
            className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 transition-colors ${
              liked
                ? "bg-primary-100 text-primary-600"
                : "text-foreground-500 hover:bg-background-100 hover:text-foreground-800"
            }`}
            aria-pressed={liked}
          >
            <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} aria-hidden="true" />
            {liked ? formatCount(post.votes + 1) : formatCount(post.votes)}
          </button>
          <span className="hidden items-center gap-1 whitespace-nowrap sm:inline-flex">
            <i className="ri-eye-line" aria-hidden="true" />
            {formatCount(post.views)}
          </span>
        </div>
      </div>
    </article>
  );
}

export default function FeedSection({ query }: FeedSectionProps) {
  const [tab, setTab] = useState("全部");

  const list = useMemo(() => {
    let l = hotPosts;
    if (tab !== "全部") l = l.filter((p) => p.category === tab);
    if (query) {
      const kw = query.toLowerCase();
      l = l.filter(
        (p) =>
          p.title.toLowerCase().includes(kw) ||
          p.summary.toLowerCase().includes(kw) ||
          p.tags.some((t) => t.toLowerCase().includes(kw)),
      );
    }
    return l;
  }, [tab, query]);

  return (
    <section id="hot" className="w-full bg-background-100/60 py-12 md:py-16" aria-label="头条热帖">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid gap-10 lg:grid-cols-3">
          {/* 左侧信息流 */}
          <div className="lg:col-span-2">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600">
                  <i className="ri-fire-fill" aria-hidden="true" />
                  正在发酵
                </span>
                <h2 className="mt-1 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                  头条热帖 · 前排围观
                </h2>
              </div>
            </div>

            <div className="thin-scroll mt-5 flex gap-2 overflow-x-auto pb-1">
              {tabs.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    tab === t
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            {list.length === 0 ? (
              <div className="mt-6 flex flex-col items-center rounded-2xl border border-background-200 bg-background-50 py-16 text-center">
                <i className="ri-inbox-line text-4xl text-background-400" aria-hidden="true" />
                <p className="mt-3 text-sm text-foreground-600">没有搜到相关热帖，换个关键词试试</p>
              </div>
            ) : (
              <div className="mt-6 flex flex-col gap-3">
                {list.map((p, i) => (
                  <HotPostCard key={p.id} post={p} rank={i + 1} />
                ))}
              </div>
            )}
          </div>

          {/* 右侧热搜榜 */}
          <aside className="space-y-6" aria-label="热搜话题榜">
            <div className="rounded-2xl border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-lg font-extrabold text-foreground-950">
                  <i className="ri-fire-fill text-accent-500" aria-hidden="true" />
                  热搜话题榜
                </h2>
                <span className="text-xs text-foreground-400">每10分钟更新</span>
              </div>
              <ol className="mt-4 space-y-1">
                {trends.map((t) => (
                  <li key={t.rank}>
                    <a
                      href="#thread"
                      className="group flex items-center gap-3 rounded-xl px-2 py-2.5 transition-colors hover:bg-background-100"
                    >
                      <span
                        className={`w-6 text-center font-heading text-base font-extrabold ${
                          t.rank === 1
                            ? "text-primary-500"
                            : t.rank <= 3
                              ? "text-secondary-700"
                              : "text-foreground-300"
                        }`}
                      >
                        {t.rank}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                            {t.title}
                          </span>
                          {t.tag && (
                            <span
                              className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-bold ${
                                t.tag === "爆"
                                  ? "bg-primary-500 text-background-50"
                                  : t.tag === "热"
                                    ? "bg-secondary-200 text-secondary-800"
                                    : "bg-accent-100 text-accent-700"
                              }`}
                            >
                              {t.tag}
                            </span>
                          )}
                        </span>
                        <span className="mt-0.5 block text-xs text-foreground-400">{t.heat}</span>
                      </span>
                      <span className="flex flex-col items-center text-foreground-400">
                        <i
                          className={t.delta > 0 ? "ri-arrow-up-line text-accent-500" : "ri-arrow-down-line text-secondary-500"}
                          aria-hidden="true"
                        />
                        <span className="text-[10px]">{Math.abs(t.delta)}</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ol>
              <p className="mt-3 rounded-xl bg-accent-100/70 px-3 py-2 text-xs leading-relaxed text-accent-900">
                <i className="ri-lightbulb-flash-line mr-1" aria-hidden="true" />
                榜单按讨论热度实时计算，点击话题进入围观现场发表你的观点。
              </p>
            </div>

            <div className="rounded-2xl border border-secondary-200 bg-secondary-100/70 p-4 md:p-5">
              <h3 className="flex items-center gap-2 text-sm font-bold text-secondary-900">
                <i className="ri-price-tag-3-line text-base" aria-hidden="true" />
                今日吃瓜热词
              </h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {["长风渡口", "宿舍神器", "反向旅游", "危机公关", "低功耗", "名场面", "总决赛", "神反转"].map(
                  (w) => (
                    <span
                      key={w}
                      className="cursor-pointer whitespace-nowrap rounded-full bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:text-primary-600"
                    >
                      # {w}
                    </span>
                  ),
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}